import React from 'react';
import { Link } from 'react-router-dom';
import type { CoffeeRateInterface, CoffeeBeanRef, CoffeeCapsuleRef } from '../types';

interface CardIndicatorsProps {
    coffeeData: CoffeeRateInterface;
}

/** A populated ref is an object carrying an `_id`; a bare id string is not. */
const isPopulated = <T extends { _id: string }>(x: unknown): x is T =>
    typeof x === 'object' && x !== null && '_id' in x;

/** One-word quality read of the overall score (0–10). */
const qualityWord = (n: number): string => {
    if (n >= 9) return 'Excellent';
    if (n >= 8) return 'Great';
    if (n >= 7) return 'Good';
    if (n >= 6) return 'Decent';
    if (n >= 4.5) return 'OK';
    return 'Meh';
};

/** Human distance: metres under ~1 km, otherwise km with one decimal. */
const formatDistance = (m?: number): string | null => {
    if (typeof m !== 'number' || !isFinite(m) || m < 0) return null;
    if (m < 950) return `${Math.round(m / 10) * 10} m`;
    return `${(m / 1000).toFixed(1)} km`;
};

/**
 * Compact info indicators sitting under the type/shop line. Each is a small
 * chip; links route where it makes sense (chain page, shop page). The set is
 * deliberately conditional so a card never shows a meaningless chip.
 */
export const CardIndicators: React.FC<CardIndicatorsProps> = ({ coffeeData }) => {
    const ref = coffeeData.coffeeShopRef;
    const isSelfMade = !!coffeeData.isSelfMade;
    const totalRatings = ref?.totalRatings;
    const isNewShop =
        !!ref && !isSelfMade && !ref.chainId &&
        typeof totalRatings === 'number' && totalRatings <= 1;
    const distance = formatDistance(coffeeData.distance);

    // detailed = per-category sliders; quick = single value. Mirror the backend:
    // quick when quickRate is set or the only stored key is the synthetic one.
    const categoryKeys = coffeeData.rating
        ? Object.keys(coffeeData.rating).filter((k) => k !== 'quick-rate')
        : [];
    const isQuick = typeof coffeeData.quickRate === 'number' || categoryKeys.length === 0;

    const chips: React.ReactNode[] = [];

    chips.push(
        <span key='quality' className='chip chip--accent'>
            {qualityWord(coffeeData.overallRating)}
        </span>,
    );

    if (isSelfMade) {
        chips.push(<span key='home' className='chip chip--home'>🏠 Home made</span>);

        // For verified-master home ratings, surface the bean / capsule used as
        // subtle tags right next to the "Home made" chip (only when populated).
        if (coffeeData.kind === 'home' && coffeeData.home) {
            const bean = coffeeData.home.beanRef;
            const capsule = coffeeData.home.capsuleRef;
            if (isPopulated<CoffeeBeanRef>(bean)) {
                chips.push(
                    <span key='bean' className='chip chip--bean'>
                        🫘 {bean.roaster || bean.name}
                    </span>,
                );
            }
            if (isPopulated<CoffeeCapsuleRef>(capsule)) {
                chips.push(
                    <span key='capsule' className='chip chip--capsule'>
                        💊 {capsule.brand} {capsule.name}
                    </span>,
                );
            }
        }
    } else if (isNewShop) {
        chips.push(<span key='new' className='chip chip--new'>✦ New spot</span>);
    } else if (ref?.chainId) {
        chips.push(
            <Link key='chain' to={`/chains/${ref.chainId._id}`} className='chip chip--chain'>
                ⛓ {ref.chainId.name}
            </Link>,
        );
    }

    if (!isSelfMade && typeof totalRatings === 'number' && totalRatings > 1 && ref?._id) {
        chips.push(
            <Link key='count' to={`/coffeeshops/${ref._id}`} className='chip chip--link'>
                ☕ {totalRatings} ratings
            </Link>,
        );
    }

    if (distance) {
        chips.push(<span key='dist' className='chip'>📍 {distance}</span>);
    }

    chips.push(
        <span key='mode' className={`chip ${isQuick ? 'chip--quick' : 'chip--detailed'}`}>
            {isQuick ? 'Quick rate' : 'Detailed'}
        </span>,
    );

    return <div className='card-chips'>{chips}</div>;
};
